/**
 * EditSubscriptionModal - Modal premium de edição de Recorrência (Assinatura)
 *
 * Arquitetura de edição em 3 zonas de segurança:
 *  - ZONA LIVRE     → sempre editável (descrição, observações, dias antes emissão,
 *                     próximo vencimento futuro)
 *  - ZONA SENSÍVEL  → afeta SOMENTE ciclos `aguardando_emissao` (itens, meio de
 *                     pagamento, conta corrente, ciclos_total)
 *  - ZONA BLOQUEADA → imutável (cliente, frequência, tipo de prazo, 1º vencimento)
 *
 * Fluxo:
 *  1. Pré-popula a partir de useAssinaturaDetalhes (já carregado no modal pai).
 *  2. Captura `updated_at` como ETag (optimistic locking).
 *  3. Gera `idempotency_key` (uuid v4) por sessão de edição.
 *  4. Submete via useAssinaturaActions.atualizar → RPC `update_financeiro_assinatura`.
 *  5. Hook dispara `financial-charge update_payment` em paralelo para ciclos já
 *     emitidos (best-effort, não bloqueante).
 *
 * @author Módulo Financeiro - Recorrência
 * @version 1.2.0
 * RELEASE: 2026-09-12 v1.3.0 - Inverte ordem: Conta corrente antes do Meio de
 *          pagamento; meios filtrados pela conta (globais sem vínculo aparecem
 *          em todas); troca de conta desmarca meio incompatível.
 * RELEASE: 2026-08-18 v1.2.0 - Resolve o contato principal na edição para
 *          disponibilizar todos os veículos vinculados ao cliente.
 * RELEASE: 2026-06-27 v1.1.0 - Validação não bloqueia campos pré-existentes
 *          (proximo_vencimento, meio_pagamento_id, conta_corrente_id) e exibe
 *          tooltip com motivo do bloqueio quando Salvar fica desabilitado.
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 6 do módulo Recorrência).
 */
import { FinanceiroVehiclesBlock } from '@/components/financial/FinanceiroVehiclesBlock';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Repeat,
  Loader2,
  AlertTriangle,
  CalendarDays,
  CreditCard,
  Building2,
  FileText,
  Wrench,
  Plus,
  Lock,
  ShieldAlert,
  Info,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { ProductCatalogModal } from '@/components/crm/deals/modal/tabs/products/ProductCatalogModal';
import { DealItemsSummary } from '@/components/crm/deals/modal/tabs/products/DealItemsSummary';
import { useAssinaturaDetalhes } from '@/hooks/useAssinaturaDetalhes';
import { useAssinaturaActions, UpdateAssinaturaPayload } from '@/hooks/useAssinaturaActions';
import { useFinanceiroMeiosPagamento } from '@/hooks/useFinanceiroMeiosPagamento';
import { useFinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import { useCrmProducts, CrmProduct } from '@/hooks/useCrmProducts';
import { useCrmCustomerById } from '@/hooks/useCrmCustomers';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import {
  SubscriptionJurosSection,
  JurosValue,
  JUROS_DEFAULT,
} from '@/components/financial/subscription/SubscriptionJurosSection';
import { SubscriptionExpiracaoSection } from '@/components/financial/subscription/SubscriptionExpiracaoSection';
import { MidCycleProrationPreview } from '@/components/financial/subscription/MidCycleProrationPreview';
import { useMidCycleProrationPreview } from '@/hooks/useMidCycleProrationPreview';
import { DescricaoTemplateField } from '@/components/financial/DescricaoTemplateField';
import { persistDescricaoTemplate, hasDescricaoVariables } from '@/hooks/financial/useFinanceiroDescricaoTemplate';

const logger = createLogger('EditSubscription');

const VALOR_MINIMO_COBRANCA = 5;

interface EditSubscriptionModalProps {
  assinaturaId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Modo de desconto por item (% ou R$) — espelha CreateSubscriptionModal. */
type DiscountMode = 'percent' | 'fixed';

interface FormItem {
  id: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  /** Percentual (0–100) — usado quando modo='percent'. */
  desconto_percent: number;
  /** Valor fixo em R$ — usado quando modo='fixed'. */
  desconto_fixed: number;
  recurrence_interval: string | null;
}

const FREQ_LABEL: Record<string, string> = {
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  mensal: 'Mensal',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  bienal: 'Bienal',
};

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

/**
 * Espelha `public.calc_ciclo_data_vencimento` (v1.1.0) — usado apenas para
 * gerar PRÉVIA visual dos novos vencimentos no modal de edição. O cálculo
 * real/persistente é feito no backend ao salvar.
 *
 * Regra de negócio:
 *  - A data de vencimento depende EXCLUSIVAMENTE de `proximo_vencimento`
 *    + `frequencia`. Os campos `tipo_emissao` / `dia_emissao` controlam
 *    apenas QUANDO a cobrança é enviada ao provedor, NÃO o vencimento.
 *
 *  - semanal       → base + offset*7 dias
 *  - mensal / else → base + offset meses (com clamp ao último dia válido)
 *  - anual         → base + offset anos  (com clamp ao último dia válido)
 */
function calcCicloDataVencimentoPreview(
  cicloNumero: number,
  cicloBase: number,
  proxVencISO: string,
  frequencia: string,
): string {
  if (!proxVencISO) return '';
  const [y, m, d] = proxVencISO.split('-').map(Number);
  if (!y || !m || !d) return '';
  const offset = cicloNumero - cicloBase;
  const freq = frequencia || 'mensal';

  let ty = y, tm = m - 1, td = d; // m é 1-12, JS usa 0-11
  if (freq === 'semanal') {
    const base = new Date(Date.UTC(y, m - 1, d));
    base.setUTCDate(base.getUTCDate() + offset * 7);
    ty = base.getUTCFullYear();
    tm = base.getUTCMonth();
    td = base.getUTCDate();
  } else if (freq === 'anual') {
    ty = y + offset;
    tm = m - 1;
    td = d;
  } else {
    // mensal e fallback
    const total = (m - 1) + offset;
    ty = y + Math.floor(total / 12);
    tm = ((total % 12) + 12) % 12;
    td = d;
  }

  // Clamp ao último dia do mês resultante (matches Postgres + interval semantics)
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  td = Math.min(td, lastDay);

  const mm = String(tm + 1).padStart(2, '0');
  const dd = String(td).padStart(2, '0');
  return `${ty}-${mm}-${dd}`;
}

const formatDateBR = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export function EditSubscriptionModal({
  assinaturaId,
  open,
  onOpenChange,
}: EditSubscriptionModalProps) {
  const { data, isLoading } = useAssinaturaDetalhes(assinaturaId, open);
  const { atualizar, isBusy } = useAssinaturaActions(assinaturaId);
  const { meios } = useFinanceiroMeiosPagamento();
  const { contas } = useFinanceiroContaCorrente();
  const { products: recurringProducts } = useCrmProducts(
    { is_active: true, product_type: 'service', unit_type: 'recorrente' },
    { enabled: open },
  );

  // ===== Estados =====
  const [descricao, setDescricao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [diasAntesEmissao, setDiasAntesEmissao] = useState(12);
  // v1.2.0: modo de emissão da cobrança
  const [tipoEmissao, setTipoEmissao] = useState<'dias_antes' | 'dia_fixo'>('dias_antes');
  const [diaEmissao, setDiaEmissao] = useState<number>(1);
  const [proximoVencimento, setProximoVencimento] = useState('');
  const [meioPagamentoId, setMeioPagamentoId] = useState<string | null>(null);
  const [contaCorrenteId, setContaCorrenteId] = useState<string | null>(null);
  const [ciclosTotal, setCiclosTotal] = useState<number | null>(null);
  const [itens, setItens] = useState<FormItem[]>([]);
  /** Mapa id_item → modo de desconto (percent | fixed). Hidratado do metadata. */
  const [discountModes, setDiscountModes] = useState<Record<string, DiscountMode>>({});
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [etag, setEtag] = useState<string | null>(null);
  // v1.2.1: controla exibição da prévia de ciclos futuros na Zona sensível.
  const [previewOpen, setPreviewOpen] = useState(true);
  // v1.4.0: nova seção de Juros e Multa por atraso
  const [juros, setJuros] = useState<JurosValue>(JUROS_DEFAULT);
  // v1.4.0: dias até expiração da cobrança após o vencimento (1..60)
  const [diasExpiracao, setDiasExpiracao] = useState<number>(60);
  // v1.1.0: snapshot dos valores hidratados — usado para relaxar validações em
  // campos que o usuário NÃO alterou (ex.: proximo_vencimento já vencido em
  // recorrências antigas não deve bloquear edição de Juros/Multa).
  const [initialSnapshot, setInitialSnapshot] = useState<{
    proximoVencimento: string;
    meioPagamentoId: string | null;
    contaCorrenteId: string | null;
  } | null>(null);
  // v1.1.4: idempotency_key é gerado a cada clique em Salvar (inline no handleSubmit),
  // não mais uma vez por abertura do modal — evita unique_violation em re-tentativas
  // distintas e mantém idempotência real para retries do mesmo clique.

  // ===== Pré-popular ao carregar =====
  useEffect(() => {
    if (!data?.assinatura || !open) return;
    const a = data.assinatura;
    // Prioriza o TEMPLATE (com variáveis) quando existir; senão usa o texto renderizado
    setDescricao(((a as any).descricao_template as string) || a.descricao || '');

    setObservacoes(a.observacoes || '');
    setDiasAntesEmissao(a.dias_antes_emissao || 12);
    // v1.2.0: hidrata modo de emissão a partir do detalhe
    setTipoEmissao((a as any).tipo_emissao === 'dia_fixo' ? 'dia_fixo' : 'dias_antes');
    setDiaEmissao((a as any).dia_emissao ?? 1);
    setProximoVencimento(a.proximo_vencimento || '');
    setMeioPagamentoId(a.meio_pagamento_id);
    setContaCorrenteId(a.conta_corrente_id);
    setCiclosTotal(a.ciclos_total ?? null);
    setEtag(a.updated_at as string);
    // v1.1.0: snapshot inicial para dirty-tracking na validação
    setInitialSnapshot({
      proximoVencimento: a.proximo_vencimento || '',
      meioPagamentoId: a.meio_pagamento_id,
      contaCorrenteId: a.conta_corrente_id,
    });
    // v1.4.0: hidrata juros (jsonb) e dias_expiracao
    const j = (a as any).juros as Partial<JurosValue> | null | undefined;
    setJuros({
      enabled: Boolean(j?.enabled),
      multa_tipo: (j?.multa_tipo as JurosValue['multa_tipo']) || 'percent',
      multa_valor: Number(j?.multa_valor) || 0,
      juros_tipo: (j?.juros_tipo as JurosValue['juros_tipo']) || 'percent',
      juros_valor: Number(j?.juros_valor) || 0,
    });
    setDiasExpiracao(Number((a as any).dias_expiracao_pos_vencimento) || 60);
    // Hidrata itens + modos de desconto a partir do retorno do RPC
    // get_assinatura_details (v1.2.1), que entrega desconto_tipo e
    // desconto_percent originados de financeiro_cobranca_itens.metadata.
    const hydratedModes: Record<string, DiscountMode> = {};
    const hydratedItens: FormItem[] = (data.itens || []).map((it: any) => {
      const localId = crypto.randomUUID();
      const tipo: DiscountMode = it.desconto_tipo === 'percent' ? 'percent' : 'fixed';
      hydratedModes[localId] = tipo;
      const descontoTotal = Number(it.desconto) || 0;
      const percent = Number(it.desconto_percent) || 0;
      return {
        id: localId,
        product_id: it.product_id || '',
        product_name: it.product_name || it.descricao || '',
        product_sku: it.product_sku || null,
        descricao: it.descricao || it.product_name || '',
        quantidade: Number(it.quantidade) || 1,
        valor_unitario: Number(it.valor_unitario) || 0,
        // Se modo='percent', preserva o % original e zera o fixed (será recalculado);
        // se modo='fixed', usa o valor de desconto direto.
        desconto_percent: tipo === 'percent' ? percent : 0,
        desconto_fixed: tipo === 'fixed' ? descontoTotal : 0,
        recurrence_interval: data.assinatura?.frequencia || null,
      };
    });
    setItens(hydratedItens);
    setDiscountModes(hydratedModes);
  }, [data, open]);

  const assin = data?.assinatura;
  // A RPC de detalhes não expõe o contato; reaproveita a RPC segura do cliente.
  const { data: assinaturaCustomer } = useCrmCustomerById(assin?.customer_id ?? null);
  const ciclos = data?.ciclos || [];
  const isCancelada = assin?.status === 'cancelada' || assin?.status === 'finalizada';
  const ciclosGerados = assin?.ciclos_gerados ?? 0;
  const frequencia = assin?.frequencia || '';

  // ===== Status dos ciclos para regras de bloqueio =====
  const cyclesEmittedCount = ciclos.filter((c) =>
    ['pendente', 'aguardando_pagamento', 'pago', 'vencido', 'parcial', 'chargeback'].includes(c.status),
  ).length;
  const hasPaidOrFinal = ciclos.some((c) =>
    ['pago', 'parcial', 'chargeback', 'reembolsado'].includes(c.status),
  );
  /**
   * v1.2 (2026-06-17): Apenas assinaturas canceladas/finalizadas bloqueiam a Zona sensível.
   * A presença de ciclos pagos NÃO bloqueia mais a edição — o backend
   * (`update_financeiro_assinatura` v1.8.0 + `_regerar_itens_ciclos_aguardando_emissao`)
   * preserva ciclos pagos/vencidos/chargeback intactos e regenera apenas
   * `aguardando_emissao` reaplicando `pro_rata_factor`.
   * Ver Documentacao/Financeiro/Edicao_Recorrencia_1.0.md § 4.1 e § 5 (risco #2b).
   */
  const sensitiveBlocked = isCancelada;
  // Mantido apenas para referência informativa (banner de aviso).
  void hasPaidOrFinal;

  // ===== Cálculo de desconto por item (soma % + R$; no máx. um lado é > 0) =====
  const calcItemDiscount = useCallback((item: FormItem) => {
    const sub = item.quantidade * item.valor_unitario;
    const percentDisc = item.desconto_percent ? sub * (item.desconto_percent / 100) : 0;
    const fixedDisc = item.desconto_fixed || 0;
    return percentDisc + fixedDisc;
  }, []);

  // ===== Subtotais =====
  const subtotal = useMemo(
    () => itens.reduce((s, i) => s + i.quantidade * i.valor_unitario, 0),
    [itens],
  );
  const totalDescontos = useMemo(
    () => itens.reduce((s, i) => s + calcItemDiscount(i), 0),
    [itens, calcItemDiscount],
  );
  const valorPorCiclo = subtotal - totalDescontos;

  // ===== Prévia de ajuste pro-rata mid-cycle (Fase C) =====
  // Calcula o impacto financeiro de alterar itens enquanto existe um ciclo
  // atual já pago. Apenas leitura — RPC `preview_mid_cycle_proration`.
  const previewItensInput = useMemo(
    () =>
      itens
        .filter((i) => i.product_id)
        .map((i) => ({
          product_id: i.product_id,
          descricao: i.descricao || i.product_name,
          quantidade: i.quantidade,
          valor_unitario: i.valor_unitario,
          desconto: Number(calcItemDiscount(i).toFixed(2)),
        })),
    [itens, calcItemDiscount],
  );
  const midCyclePreview = useMidCycleProrationPreview({
    assinaturaId,
    itens: previewItensInput,
    enabled: open && !sensitiveBlocked,
  });

  // v1.3.0 — Checkbox "Aproveitar crédito" exibido quando a política mid-cycle
  // do tenant é 'ask'. O backend é a fonte de verdade — se a política for
  // 'auto' ou 'disabled' este flag é IGNORADO. Resetado quando o modal abre.
  const [consumeCreditConfirmed, setConsumeCreditConfirmed] = useState(false);
  useEffect(() => {
    if (open) setConsumeCreditConfirmed(false);
  }, [open]);

  // ===== Catálogo =====
  const handleCatalogAddItem = useCallback(
    async (productId: string, quantity: number, unitPrice: number) => {
      const prod: CrmProduct | undefined = recurringProducts.find((p) => p.id === productId);
      if (!prod) return;
      if (prod.recurrence_interval !== frequencia) {
        toast.error(
          `Frequência incompatível. Esta recorrência é ${FREQ_LABEL[frequencia] || frequencia}.`,
        );
        return;
      }
      const existing = itens.find((i) => i.product_id === productId);
      if (existing) {
        setItens((prev) =>
          prev.map((i) =>
            i.product_id === productId ? { ...i, quantidade: i.quantidade + quantity } : i,
          ),
        );
        return;
      }
      const newId = crypto.randomUUID();
      setItens((prev) => [
        ...prev,
        {
          id: newId,
          product_id: productId,
          product_name: prod.name,
          product_sku: prod.sku || null,
          descricao: prod.name,
          quantidade: quantity,
          valor_unitario: unitPrice || prod.recurring_price || 0,
          desconto_percent: 0,
          desconto_fixed: 0,
          recurrence_interval: prod.recurrence_interval,
        },
      ]);
      // Novos itens iniciam em modo 'fixed' (mesmo default do CreateSubscriptionModal).
      setDiscountModes((prev) => ({ ...prev, [newId]: 'fixed' }));
    },
    [recurringProducts, frequencia, itens],
  );
  const handleCatalogUpdateQuantity = useCallback(async (productId: string, quantity: number) => {
    setItens((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantidade: quantity } : i)),
    );
  }, []);
  const handleCatalogRemoveItem = useCallback(async (productId: string) => {
    setItens((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);
  const handleRemoveItem = useCallback((id: string) => {
    setItens((prev) => prev.filter((i) => i.id !== id));
  }, []);
  const handleUpdateField = useCallback(
    (id: string, field: keyof FormItem, value: number | string) => {
      setItens((prev) => prev.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
    },
    [],
  );

  const itensForCatalog = useMemo(
    () => itens.map((i) => ({ product_id: i.product_id, quantity: i.quantidade })),
    [itens],
  );

  // ===== Validação =====
  // v1.1.0: produz lista de motivos de invalidação (para tooltip) e flag isValid.
  // Regras "campo no futuro / campo preenchido" só se aplicam quando o usuário
  // ALTEROU o campo em relação ao snapshot hidratado — assim recorrências antigas
  // com proximo_vencimento vencido ou meio/conta nulos não bloqueiam edições em
  // outras seções (ex.: Juros e Multa, Expiração, observações).
  const { isValid, invalidReasons } = useMemo(() => {
    const reasons: string[] = [];
    if (!assin) reasons.push('Carregando dados da recorrência...');
    if (isCancelada) reasons.push('Recorrência cancelada não pode ser editada.');
    if (!descricao.trim()) reasons.push('Informe a descrição.');
    if (diasAntesEmissao < 0) reasons.push('Dias antes da emissão deve ser ≥ 0.');
    if (!proximoVencimento) {
      reasons.push('Informe o próximo vencimento.');
    } else if (
      // Só exige data futura se o usuário alterou o campo
      initialSnapshot &&
      proximoVencimento !== initialSnapshot.proximoVencimento &&
      proximoVencimento < new Date().toISOString().slice(0, 10)
    ) {
      reasons.push('O próximo vencimento não pode ser anterior a hoje.');
    }
    if (!sensitiveBlocked) {
      if (itens.length === 0) reasons.push('Adicione ao menos 1 item.');
      if (valorPorCiclo < VALOR_MINIMO_COBRANCA) {
        reasons.push('Valor por ciclo abaixo do mínimo (R$ 5,00).');
      }
      // Meio de pagamento / conta corrente: só exige se estavam preenchidos antes
      // e o usuário limpou agora. Recorrências antigas podem ter vindo nulas.
      if (!meioPagamentoId && initialSnapshot?.meioPagamentoId) {
        reasons.push('Selecione o meio de pagamento.');
      }
      if (!contaCorrenteId && initialSnapshot?.contaCorrenteId) {
        reasons.push('Selecione a conta corrente.');
      }
      if (ciclosTotal !== null && ciclosTotal < ciclosGerados) {
        reasons.push(`Ciclos totais (${ciclosTotal}) menor que já gerados (${ciclosGerados}).`);
      }
    }
    return { isValid: reasons.length === 0, invalidReasons: reasons };
  }, [
    assin,
    isCancelada,
    descricao,
    diasAntesEmissao,
    proximoVencimento,
    initialSnapshot,
    sensitiveBlocked,
    itens,
    valorPorCiclo,
    meioPagamentoId,
    contaCorrenteId,
    ciclosTotal,
    ciclosGerados,
  ]);

  // ===== Submit =====
  const handleSubmit = async () => {
    if (!isValid || !assin || !etag) return;

    // v1.1.4: nova idempotency_key por clique (não por modal aberto)
    const idemKey = crypto.randomUUID();

    const payload: UpdateAssinaturaPayload = {
      descricao,
      observacoes,
      dias_antes_emissao: diasAntesEmissao,
      tipo_emissao: tipoEmissao,
      dia_emissao: tipoEmissao === 'dia_fixo' ? diaEmissao : null,
      // v1.4.0: juros (jsonb consolidado) + expiração
      juros: juros.enabled ? (juros as unknown as Record<string, unknown>) : {},
      dias_expiracao_pos_vencimento: diasExpiracao,
      updated_at_etag: etag,
      idempotency_key: idemKey,
      // v1.3.0 — Política de consumo de crédito do cliente.
      // O backend ignora este flag quando a política é 'auto' (consome sempre)
      // ou 'disabled' (nunca consome). Só é honrado quando política='ask'.
      pro_rata: {
        consume_customer_credit: consumeCreditConfirmed,
      },
    };

    // v1.5.0: só envia `proximo_vencimento` quando o usuário realmente alterou
    // o campo. Recorrências legadas podem ter a próxima data já vencida; nesses
    // casos, editar itens/juros/meio de pagamento não deve forçar recálculo nem
    // bloquear no backend por uma data que não foi modificada nesta operação.
    if (!initialSnapshot || proximoVencimento !== initialSnapshot.proximoVencimento) {
      payload.proximo_vencimento = proximoVencimento;
    }

    // Zona sensível só se não estiver bloqueada
    if (!sensitiveBlocked) {
      payload.valor_total = valorPorCiclo;
      payload.meio_pagamento_id = meioPagamentoId || undefined;
      payload.conta_corrente_id = contaCorrenteId || undefined;
      payload.ciclos_total = ciclosTotal;
      payload.itens = itens.map((i) => {
        const mode: DiscountMode = discountModes[i.id] ?? 'fixed';
        return {
          product_id: i.product_id,
          descricao: i.descricao,
          quantidade: i.quantidade,
          valor_unitario: i.valor_unitario,
          // desconto final em R$ (calculado a partir do modo escolhido)
          desconto: Number(calcItemDiscount(i).toFixed(2)),
          // metadata persistida para que get_assinatura_details devolva o toggle %/R$ fielmente
          desconto_tipo: mode,
          desconto_percent: mode === 'percent' ? Number((i.desconto_percent || 0).toFixed(4)) : 0,
        };
      });
    }

    try {
      logger.info('submit_diff', {
        assinaturaId,
        sensitiveBlocked,
        ciclosFuturos: ciclos.filter((c) => c.status === 'aguardando_emissao').length,
      });
      await atualizar.mutateAsync(payload);
      // Persistir template de descrição (quando contiver variáveis) para que
      // os próximos ciclos sejam renderizados com dados atualizados.
      if (hasDescricaoVariables(descricao)) {
        await persistDescricaoTemplate({ template: descricao, assinaturaId });
      }

      logger.info('save_success', { assinaturaId });
      onOpenChange(false);
    } catch (err: any) {
      logger.error('save_error', { error: String(err?.message || err) });
    }
  };

  // ===== Prévia de ciclos futuros (Zona sensível) =====
  // IMPORTANTE: este useMemo precisa ficar ANTES de qualquer early return
  // para respeitar as Rules of Hooks (mesma quantidade de hooks por render).
  // Recalcula data_vencimento client-side espelhando `calc_ciclo_data_vencimento`
  // do backend (v1.1.0). Depende APENAS de proximoVencimento + frequencia.
  // Os campos tipoEmissao/diaEmissao controlam a janela de emissão no provedor,
  // não afetam o vencimento.
  const ciclosFuturosPreview = useMemo(() => {
    const futuros = ciclos
      .filter((c) => c.status === 'aguardando_emissao')
      .sort((a, b) => a.ciclo_numero - b.ciclo_numero);
    if (futuros.length === 0 || !proximoVencimento || !frequencia) return [];
    const cicloBase = futuros[0].ciclo_numero;
    return futuros.map((c) => {
      const novaData = calcCicloDataVencimentoPreview(
        c.ciclo_numero,
        cicloBase,
        proximoVencimento,
        frequencia,
      );
      return {
        cobranca_id: c.cobranca_id,
        ciclo_numero: c.ciclo_numero,
        data_atual: c.data_vencimento,
        data_nova: novaData,
        mudou_data: novaData !== c.data_vencimento,
        valor_atual: c.valor_total,
        valor_novo: sensitiveBlocked ? c.valor_total : valorPorCiclo,
        mudou_valor: !sensitiveBlocked && Math.abs(valorPorCiclo - c.valor_total) > 0.005,
      };
    });
  }, [ciclos, proximoVencimento, frequencia, sensitiveBlocked, valorPorCiclo]);

  const previewHasChanges = ciclosFuturosPreview.some((c) => c.mudou_data || c.mudou_valor);

  /**
   * v2026-09-12 (filtro por conta): ao trocar a conta corrente, desmarca o
   * meio de pagamento se ele for vinculado a outra conta (meios globais,
   * sem vínculo, são preservados). Hook declarado ANTES do early return.
   */
  useEffect(() => {
    if (!meioPagamentoId) return;
    const meioAtual = meios.find((m) => m.id === meioPagamentoId);
    if (meioAtual?.conta_corrente_id && meioAtual.conta_corrente_id !== contaCorrenteId) {
      setMeioPagamentoId(null);
    }
  }, [contaCorrenteId, meioPagamentoId, meios]);

  // Early return SOMENTE após todos os hooks declarados acima
  if (!assinaturaId) return null;

  const meiosAtivos = meios.filter((m) => m.is_active);
  // v2026-09-12: contas sem integração (controle manual) também são listadas
  const contasComProvider = contas.filter((c) => c.is_active);
  // v2026-09-12: meios da conta selecionada + globais (sem vínculo de conta)
  const meiosDaConta = meiosAtivos.filter(
    (m) => !m.conta_corrente_id || m.conta_corrente_id === contaCorrenteId,
  );
  const ciclosAguardando = ciclos.filter((c) => c.status === 'aguardando_emissao').length;




  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            'p-0 gap-0 overflow-hidden',
            'sm:max-w-5xl sm:rounded-lg',
            'max-sm:fixed max-sm:inset-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:h-[100dvh] max-sm:max-w-none max-sm:rounded-none',
            'flex flex-col h-full sm:max-h-[90vh]',
          )}
        >
          <DialogHeader className="px-5 py-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <Repeat className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base flex items-center gap-2">
                  Editar Recorrência
                  {assin && (
                    <span className="font-mono text-xs text-muted-foreground">
                      #{assin.id.substring(0, 8).toUpperCase()}
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Alterações em itens/valores afetam somente ciclos ainda não emitidos.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-0">
            {isLoading || !assin ? (
              <div className="p-5 space-y-3">
                <div className="h-20 rounded bg-muted animate-pulse" />
                <div className="h-40 rounded bg-muted animate-pulse" />
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Banners de contexto */}
                {hasPaidOrFinal && (
                  <InlineNotice variant="info" icon={ShieldAlert} className="text-xs">
                    Esta recorrência já possui ciclos pagos/emitidos. Alterações em
                    itens, valores, meio de pagamento e total de ciclos afetam
                    <strong> somente os ciclos ainda não emitidos</strong>
                    (aguardando emissão). Ciclos pagos, vencidos ou em chargeback
                    permanecem intactos por design — o fator pro-rata preservado por
                    ciclo é reaplicado automaticamente.
                  </InlineNotice>
                )}
                {cyclesEmittedCount > 0 && !hasPaidOrFinal && (
                  <InlineNotice variant="info" className="text-xs">
                    {cyclesEmittedCount} ciclo(s) já emitido(s) no provider serão
                    sincronizados automaticamente quando aplicável.
                  </InlineNotice>
                )}

                {/* ===== ZONA BLOQUEADA ===== */}
                <section className="rounded-lg border border-dashed border-border bg-muted/20 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                      Campos imutáveis
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-[10px] text-muted-foreground">Cliente</p>
                      <p className="font-medium truncate">{data?.customer?.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Frequência</p>
                      <p className="font-medium">{FREQ_LABEL[frequencia] || frequencia}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Tipo de prazo</p>
                      <p className="font-medium capitalize">{assin.tipo_prazo}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">1º vencimento</p>
                      <p className="font-medium">
                        {assin.primeiro_vencimento
                          ? assin.primeiro_vencimento.split('-').reverse().join('/')
                          : '—'}
                      </p>
                    </div>
                  </div>
                </section>

                {/* ===== ZONA LIVRE ===== */}
                <section className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold">Zona livre</h3>
                    <Badge variant="outline" className="text-[10px]">
                      sempre editável
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Descrição *</Label>
                    {/* Suporte a variáveis dinâmicas: o template fica salvo e cada
                        ciclo emitido renderiza a descrição atualizada. */}
                    <DescricaoTemplateField
                      value={descricao}
                      onChange={setDescricao}
                      maxLength={200}
                      assinaturaId={assinaturaId}
                    />
                  </div>

                  <div>
                    <Label className="text-xs mb-1 block">Observações</Label>
                    <Textarea
                      value={observacoes}
                      onChange={(e) => setObservacoes(e.target.value)}
                      rows={2}
                      maxLength={500}
                      className="resize-none"
                    />
                  </div>
                  {/* Veículos vinculados à assinatura (módulo Veículos) */}
                  <FinanceiroVehiclesBlock
                    assinaturaId={assinaturaId}
                    customerId={assin?.customer_id ?? null}
                    contactId={assinaturaCustomer?.main_contact_id ?? null}
                    editable
                    enabled={open}
                  />
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs mb-1 flex items-center gap-1">
                          <CalendarDays className="inline h-3 w-3 mr-1" />
                          Próximo vencimento *
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              Define a data do próximo ciclo. Cobranças futuras em "aguardando
                              emissão" serão recalculadas conforme a frequência. Cobranças já
                              emitidas no provedor (Asaas/Stripe) serão marcadas para sincronização
                              automática da nova data.
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <Input
                          type="date"
                          value={proximoVencimento}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setProximoVencimento(e.target.value)}
                        />
                      </div>
                      {assin.tipo_prazo === 'determinado' && (
                        <div>
                          <Label className="text-xs mb-1 block">
                            Ciclos totais
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="inline h-3 w-3 ml-1 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">
                                Mínimo: {ciclosGerados} (ciclos já gerados). Reduzir cancela
                                automaticamente os ciclos excedentes em "aguardando emissão".
                              </TooltipContent>
                            </Tooltip>
                          </Label>
                          <Input
                            type="number"
                            min={ciclosGerados}
                            value={ciclosTotal ?? ''}
                            onChange={(e) =>
                              setCiclosTotal(e.target.value ? Number(e.target.value) : null)
                            }
                          />
                          {ciclosTotal !== null && ciclosTotal < ciclosGerados && (
                            <p className="text-[11px] text-destructive mt-1">
                              Não pode ser menor que {ciclosGerados}.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs mb-1 flex items-center gap-1">
                        Emissão da cobrança
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            Quando o sistema vai gerar (status "pendente") cada fatura recorrente:
                            N dias antes do vencimento OU sempre num dia fixo do mês. Em meses sem
                            o dia escolhido (ex.: 31/02), o sistema usa o último dia do mês.
                          </TooltipContent>
                        </Tooltip>
                      </Label>
                      <RadioGroup
                        value={tipoEmissao}
                        onValueChange={(v) => setTipoEmissao(v as 'dias_antes' | 'dia_fixo')}
                        className="flex flex-wrap items-center gap-4 mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="dias_antes" id="emis-dias-antes-edit" />
                          <Label htmlFor="emis-dias-antes-edit" className="text-xs cursor-pointer">
                            Dias antes do vencimento
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="dia_fixo" id="emis-dia-fixo-edit" />
                          <Label htmlFor="emis-dia-fixo-edit" className="text-xs cursor-pointer">
                            Dia específico do mês
                          </Label>
                        </div>
                      </RadioGroup>
                      {tipoEmissao === 'dias_antes' ? (
                        <Input
                          type="number"
                          min={0}
                          max={60}
                          value={diasAntesEmissao}
                          onChange={(e) =>
                            setDiasAntesEmissao(Math.max(0, Math.min(60, Number(e.target.value) || 0)))
                          }
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Dia</span>
                          <Input
                            type="number"
                            min={1}
                            max={31}
                            value={diaEmissao}
                            onChange={(e) =>
                              setDiaEmissao(Math.max(1, Math.min(31, Number(e.target.value) || 1)))
                            }
                            className="w-20"
                          />
                          <span className="text-xs text-muted-foreground">do mês</span>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <Separator />

                {/* ===== ZONA SENSÍVEL ===== */}
                <section className={cn('space-y-3', sensitiveBlocked && 'opacity-50 pointer-events-none')}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-amber-600" />
                      <h3 className="text-sm font-semibold">Zona sensível</h3>
                      <Badge variant="outline" className="text-[10px] border-amber-300">
                        afeta apenas ciclos futuros ({ciclosAguardando})
                      </Badge>
                    </div>
                    {ciclosFuturosPreview.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreviewOpen((v) => !v)}
                        className="h-7 text-xs gap-1"
                      >
                        {previewOpen ? (
                          <>
                            <ChevronUp className="h-3.5 w-3.5" />
                            Ocultar prévia
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3.5 w-3.5" />
                            Ver prévia dos ciclos
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* ===== PRÉVIA DE CICLOS FUTUROS ===== */}
                  {previewOpen && ciclosFuturosPreview.length > 0 && (
                    <div className="rounded-md border bg-muted/20">
                      {previewHasChanges && (
                        <div className="px-3 py-2 border-b">
                          <InlineNotice variant="warning">
                            Datas e valores em <strong>prévia</strong> — serão aplicados ao
                            clicar em <strong>Salvar alterações</strong>.
                          </InlineNotice>
                        </div>
                      )}
                      <div className="max-h-64 overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                            <tr className="text-left text-muted-foreground">
                              <th className="px-3 py-2 font-medium w-12">#</th>
                              <th className="px-3 py-2 font-medium">Vencimento atual</th>
                              <th className="px-3 py-2 font-medium">Novo vencimento</th>
                              <th className="px-3 py-2 font-medium text-right">Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ciclosFuturosPreview.map((c) => (
                              <tr key={c.cobranca_id} className="border-t">
                                <td className="px-3 py-1.5 font-mono text-muted-foreground">
                                  #{c.ciclo_numero}
                                </td>
                                <td className="px-3 py-1.5">
                                  <span className={cn(c.mudou_data && 'line-through text-muted-foreground')}>
                                    {formatDateBR(c.data_atual)}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5">
                                  {c.mudou_data ? (
                                    <span className="inline-flex items-center gap-1 font-semibold text-amber-700 dark:text-amber-400">
                                      <ArrowRight className="h-3 w-3" />
                                      {formatDateBR(c.data_nova)}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  {c.mudou_valor ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="line-through text-muted-foreground">
                                        {formatBRL(c.valor_atual)}
                                      </span>
                                      <ArrowRight className="h-3 w-3 text-amber-600" />
                                      <span className="font-semibold text-amber-700 dark:text-amber-400">
                                        {formatBRL(c.valor_novo)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span>{formatBRL(c.valor_atual)}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}


                  {/* Itens */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Wrench className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-semibold">Itens da recorrência</span>
                      </div>
                      {itens.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCatalogOpen(true)}
                          className="h-7 text-xs"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Adicionar
                        </Button>
                      )}
                    </div>
                    {/*
                      Aviso (v1.8.0): alterações no contrato refletem apenas em
                      ciclos futuros / aguardando emissão. Cobranças já pagas,
                      em aberto ou vencidas mantêm o snapshot original (imutável).
                    */}
                    {!sensitiveBlocked && (
                      <InlineNotice variant="info" className="mb-2 text-xs">
                        Alterações em itens (adicionar, remover, ajustar quantidade ou valor)
                        valem somente para ciclos futuros e ciclos ainda aguardando emissão.
                        Cobranças já pagas, em aberto ou vencidas permanecem inalteradas.
                      </InlineNotice>
                    )}
                    <DealItemsSummary
                      variant="subscription"
                      items={itens.map((i) => ({
                        id: i.id,
                        product_id: i.product_id,
                        quantity: i.quantidade,
                        unit_price: i.valor_unitario,
                        discount_percent: i.desconto_percent || null,
                        discount_fixed: i.desconto_fixed || null,
                        // Hidrata o modo persistido em metadata para que o toggle abra fielmente em %/R$
                        discount_mode: discountModes[i.id] ?? null,
                        total: i.quantidade * i.valor_unitario - calcItemDiscount(i),
                        product: {
                          id: i.product_id,
                          name: i.product_name,
                          sku: i.product_sku,
                          product_type: 'service',
                          unit_type: 'recorrente',
                          recurring_price: i.valor_unitario,
                          recurrence_interval: i.recurrence_interval as any,
                          term_type: null,
                          term_quantity: null,
                          term_unit: null,
                        },
                      }))}
                      currency="BRL"
                      readOnly={false}
                      onUpdateQuantity={(id, qty) =>
                        handleUpdateField(id, 'quantidade', Math.max(1, qty))
                      }
                      onUpdateDiscount={(id, percent, fixed) => {
                        // Atualiza ambos os lados — DealItemsSummary zera o lado oposto ao alternar.
                        handleUpdateField(id, 'desconto_percent', percent || 0);
                        handleUpdateField(id, 'desconto_fixed', fixed || 0);
                        // Sincroniza modo: se vier percent>0 trava em 'percent', senão 'fixed'.
                        setDiscountModes((prev) => ({
                          ...prev,
                          [id]: (percent || 0) > 0 ? 'percent' : 'fixed',
                        }));
                      }}
                      onRemove={(id) => handleRemoveItem(id)}
                      onOpenCatalog={() => setCatalogOpen(true)}
                    />

                    {valorPorCiclo > 0 && valorPorCiclo < VALOR_MINIMO_COBRANCA && (
                      <Alert variant="destructive" className="mt-2">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="text-xs">
                          Valor mínimo por ciclo é <strong>R$ 5,00</strong>.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Fase C: Prévia do ajuste pro-rata mid-cycle */}
                    <div className="mt-2">
                      <MidCycleProrationPreview
                        data={midCyclePreview.data}
                        isLoading={midCyclePreview.isFetching}
                        consumeCredit={consumeCreditConfirmed}
                        onConsumeCreditChange={setConsumeCreditConfirmed}
                      />
                    </div>
                  </div>

                  {/* Conta corrente + Meio de pagamento (conta primeiro: filtra os meios) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block">
                        <Building2 className="inline h-3 w-3 mr-1" />
                        Conta corrente
                      </Label>
                      <Select
                        value={contaCorrenteId || ''}
                        onValueChange={(v) => setContaCorrenteId(v || null)}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>
                          {contasComProvider.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <span>{c.nome}</span>
                                {/* Sem provider => controle manual */}
                                <Badge variant="outline" className="text-[10px]">
                                  {c.provider_slug || 'Manual'}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">
                        <CreditCard className="inline h-3 w-3 mr-1" />
                        Meio de pagamento
                      </Label>
                      <Select
                        value={meioPagamentoId || ''}
                        onValueChange={(v) => setMeioPagamentoId(v || null)}
                        disabled={!contaCorrenteId}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              contaCorrenteId
                                ? 'Selecione...'
                                : 'Selecione a conta primeiro'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {meiosDaConta.map((m) => {
                            const Icon = PaymentMethodIcon[m.tipo_pagamento] || DefaultPaymentIcon;
                            return (
                              <SelectItem key={m.id} value={m.id}>
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  <span>{m.nome}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>

                <Separator />

                {/* v1.4.0: Juros e Multa + Expiração (colapsáveis) */}
                <SubscriptionJurosSection value={juros} onChange={setJuros} />
                <SubscriptionExpiracaoSection value={diasExpiracao} onChange={setDiasExpiracao} />

                {/* Catálogo */}
                <ProductCatalogModal
                  open={catalogOpen}
                  onOpenChange={setCatalogOpen}
                  dealId=""
                  currency="BRL"
                  dealItems={itensForCatalog}
                  onAddItem={handleCatalogAddItem}
                  onUpdateItemQuantity={handleCatalogUpdateQuantity}
                  onRemoveItem={handleCatalogRemoveItem}
                  onlyRecurring
                />
              </div>
            )}
          </ScrollArea>

          <div className="p-4 border-t shrink-0 flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              {!sensitiveBlocked && valorPorCiclo > 0 && (
                <span>
                  Novo valor por ciclo:{' '}
                  <strong className="text-foreground">{formatBRL(valorPorCiclo)}</strong>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
                Cancelar
              </Button>
              {/* v1.1.0: Tooltip mostra motivos do bloqueio quando Salvar está desabilitado */}
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* span wrapper para permitir tooltip em botão disabled */}
                  <span tabIndex={!isValid && !isBusy ? 0 : -1}>
                    <Button onClick={handleSubmit} disabled={!isValid || isBusy}>
                      {atualizar.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                      Salvar alterações
                    </Button>
                  </span>
                </TooltipTrigger>
                {!isValid && invalidReasons.length > 0 && (
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="font-medium text-xs mb-1">Para salvar, ajuste:</p>
                    <ul className="text-xs list-disc pl-4 space-y-0.5">
                      {invalidReasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

export default EditSubscriptionModal;
